"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.checkSampleLaunch = checkSampleLaunch;

var _copyContent = _interopRequireDefault(require("../../data/copyContent"));

var _formatErrors = _interopRequireDefault(require("../../utilities/formatErrors"));

var _checkExperimentReset = _interopRequireDefault(require("../../utilities/checkExperimentReset"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/*
*  Checks Experiment Sampling and Allocation
*  Expected GQL Shape:
*
*  experiment(id: $id) {
      environmentSamplings {
        environmentSamplingToVariants {
          weight
        }
      }
*/
function checkSampleLaunch({
  experiment,
  environmentId,
  sampleAllocation: proposedSampleAllocation
}) {
  const {
    environmentSamplings,
    metrics,
    experimentTypeId,
    experimentStatusId,
    experimentMetrics,
    quantumMetrics,
    isCmsExperiment
  } = experiment;
  const {
    sampleLaunch: {
      errorOneVariantWithWeightAboveZero
    }
  } = _copyContent.default;
  const {
    environmentSamplingToVariants,
    allocationHistory
  } = environmentSamplings.find(sampling => sampling.environment.id === environmentId);
  const checks = {
    errorOneVariantWithWeightAboveZero: environmentSamplingToVariants.find(variant => variant.weight !== 0) ? true : errorOneVariantWithWeightAboveZero
  }; // Allocation Guardrail scenarios

  const rails = {
    scenario: -1,
    needsReset: false,
    needsCancel: false,
    warnAboutLowering: false,
    affectedMetrics: null,
    warnVariantCancelled: false,
    cancelledVariantId: null,
    needsLaunchReminder: false,
    needsSoakingWarn: false
  };
  const sortedAllocationHistory = [...allocationHistory].sort(({
    id: idA
  }, {
    id: idB
  }) => {
    if (idA > idB) {
      return -1;
    } else {
      return 1;
    }
  });

  if (!isCmsExperiment && environmentId === 1) {
    let currentAllocation = null;

    if (sortedAllocationHistory && sortedAllocationHistory.length > 0) {
      currentAllocation = sortedAllocationHistory[0];
    }

    if (proposedSampleAllocation > 0.01 && currentAllocation && currentAllocation.sampleAllocation <= 0.01) {
      const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours x 60 mins x 60000 milliseconds (amount in a min)

      const createdTime = currentAllocation.createdTime;
      const now = new Date();
      const oneDayBefore = new Date(now - twentyFourHours);

      if (createdTime > oneDayBefore.toISOString()) {
        rails.needsSoakingWarn = true;
      }
    } else if (proposedSampleAllocation > 0.01 && !currentAllocation) {
      rails.needsSoakingWarn = true;
    } else if (proposedSampleAllocation !== 0 && 0.01 >= proposedSampleAllocation && (currentAllocation && 0 === currentAllocation.sampleAllocation || sortedAllocationHistory && !sortedAllocationHistory.length)) {
      rails.needsLaunchReminder = true;
    }
  }

  if ((0, _checkExperimentReset.default)({
    experimentTypeId,
    experimentStatusId,
    environmentId
  })) {
    return {
      checks,
      errors: (0, _formatErrors.default)(checks),
      rails
    };
  }

  if (allocationHistory && allocationHistory.length && allocationHistory[0].sampleAllocation > 0) {
    const {
      sampleAllocation: currentSampleAllocation
    } = allocationHistory[0];

    if (proposedSampleAllocation != undefined) {
      if (proposedSampleAllocation === 0) {
        // Scenario #1: dialing allocation down to zero requires cancel
        rails.needsCancel = true;
        rails.scenario = 1;
      } else if (proposedSampleAllocation < currentSampleAllocation && metrics) {
        // Scenario #4 - If lowering sample allocation
        // check and warn about affected metrics,
        // only enabled metrics on exp sourced from metric_agg
        if ((experimentMetrics && experimentMetrics.length === 0 || !experimentMetrics) && metrics) {
          rails.affectedMetrics = metrics.filter(({
            isEnabled,
            aggName
          }) => isEnabled && aggName === 'metric');
        } else if (quantumMetrics && quantumMetrics.metricDefinitions) {
          rails.affectedMetrics = quantumMetrics.metricDefinitions.filter(metric => {
            const result = experimentMetrics.find(({
              metricUuid
            }) => metricUuid === metric._id); // Verify the result is selected and has a metric agg

            if (result && metric.sourceTable === 'venona_metric_agg') {
              return metric;
            }

            return false;
          });
        }

        rails.warnAboutLowering = rails.affectedMetrics.length > 0;
        rails.scenario = 4;
      } else if (allocationHistory.length > 1) {
        const {
          sampleAllocation: olderSampleAllocation
        } = allocationHistory[1]; // Scenario #4 - If raising sample allocation after
        // lowering it in the last change, require reset

        if (proposedSampleAllocation > currentSampleAllocation && olderSampleAllocation != undefined && currentSampleAllocation < olderSampleAllocation) {
          rails.scenario = 4;
          rails.needsReset = true;
        }
      }
    } // Scenario #3: variants being mixed require a reset


    const {
      variantConfig
    } = allocationHistory[0];
    const oldVariants = JSON.parse(variantConfig);
    const variantWeightingChanged = checkForVariantWeightingChange(environmentSamplingToVariants, oldVariants, proposedSampleAllocation);
    environmentSamplingToVariants.forEach(({
      weight: proposedWeight,
      variantId
    }) => {
      // Find the variant in the oldVariants
      const oldVariant = oldVariants.find(({
        id
      }) => id === variantId);

      if (!oldVariant) {
        // Scenario #3: adding a variant mixes variant weights neccesitates reset
        rails.needsReset = true;
        rails.scenario = 3;
      } else if (proposedWeight != oldVariant.weight) {
        if (proposedWeight <= 0) {
          // Scenario #2: If the user has allocated before, and is now dialing
          // a variant to zero, warn them of the population change
          rails.scenario = 2;
          rails.warnVariantCancelled = true;
          rails.cancelledVariantId = variantId;
        } else {
          // Scenario #3: mixing variant weights neccesitates reset
          rails.scenario = 3; // only trigger a reset if the relative weighting of variants has changed

          if (variantWeightingChanged) {
            rails.needsReset = true;
          } else {
            rails.needsReset = false;
          }
        }
      }
    });
  }

  function checkForVariantWeightingChange(newVariants, oldVariants, sampleAllocation) {
    const oldVariantsTotalWeight = Object.keys(oldVariants).reduce((accumulator, variant) => accumulator + oldVariants[variant].weight, 0);
    const newVariantsTotalWeight = Object.keys(newVariants).reduce((accumulator, variant) => accumulator + newVariants[variant].weight, 0);
    const newVariantsPercentageWeights = newVariants.map(variant => {
      return variant['weight'] / newVariantsTotalWeight * sampleAllocation;
    });
    const oldVariantsPercentageWeights = oldVariants.map(variant => {
      return variant['weight'] / oldVariantsTotalWeight * sampleAllocation;
    });
    return !newVariantsPercentageWeights.every((val, index) => val === oldVariantsPercentageWeights[index]);
  }

  const errors = (0, _formatErrors.default)(checks);
  return {
    checks,
    errors,
    rails
  };
}